import { Link } from "react-router";
import type { MonthLandingQuoteLinesResponse } from "@shared-core/data/api-types";
import {
  formatCount,
  formatCurrencyGbp,
  formatCurrencyGbpCompact,
} from "@shared-core/domain/formatters";
import {
  formatSnapshotDay,
  type MonthLandingMetric,
} from "@shared-core/domain/month-landing";
import {
  distinctQuoteCount,
  quoteLineAmount,
  quoteLinesTotal,
  quoteTotalsThisMonth,
  unquotedBaseline,
} from "@shared-core/domain/month-landing-quote-lines";

// The quotes behind Month Landing's "Projected, not yet booked" band — the
// drill-down Ian asked for: "there is still £20k expected from Ramsay spread
// over the following days", in place of one opaque bar.
//
// One row per quote-day, because that is the grain the chart draws: the
// win-rate curve spreads a quote's remaining conversion over the days its
// increments land, so a quote legitimately occupies several rows. The quote's
// whole month total rides on every one of its rows so a repeated quote number
// reads as one quote spread out rather than as several quotes.
//
// Presentational only — the tab owns the fetch, the CSV and the band figure.
// Follows the drill pattern already on this page's neighbour
// (WallboardReconciliationView): tie-out header, capped table, CSV for the
// full population.

export type MonthLandingQuoteLinesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; lines: MonthLandingQuoteLinesResponse };

// The band is two projection layers and only the first has quotes behind it,
// so the header must name both parts. A reader who finds a gap between the
// band and this table, with nothing accounting for it, stops trusting both.
const ON_SCREEN_LINE_LIMIT = 50;

export interface MonthLandingQuoteLinesPanelProps {
  state: MonthLandingQuoteLinesState;
  metric: MonthLandingMetric;
  /** The band the table is drilling into, as displayed on the chart above. */
  bandTotal: number;
  /** The chart's own label for that band — passed in so the two cannot drift. */
  bandLabel: string;
  /** "October 2026" — the month as the rest of the page names it. */
  monthLabel: string;
  onRetry: () => void;
  onDownloadCsv: () => void;
}

export function MonthLandingQuoteLinesPanel({
  state,
  metric,
  bandTotal,
  bandLabel,
  monthLabel,
  onRetry,
  onDownloadCsv,
}: MonthLandingQuoteLinesPanelProps) {
  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-8 text-center text-sm">
        Loading the quotes behind the projection…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex flex-col items-center justify-center gap-2 rounded-lg border p-8 text-center">
        <p className="text-destructive text-sm">
          Couldn’t load the quotes behind the projection.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const { lines } = state;
  const quotesTotal = quoteLinesTotal(lines, metric);
  const baseline = unquotedBaseline(bandTotal, quotesTotal);
  const quoteTotals = quoteTotalsThisMonth(lines.lines, metric);
  const shown = lines.lines.slice(0, ON_SCREEN_LINE_LIMIT);
  const hasMoreThanShown = lines.lines.length > shown.length;
  const quoteCount = distinctQuoteCount(lines.lines);
  const metricWord = metric === "contribution" ? "contribution" : "value";

  return (
    <div className="border-border bg-surface overflow-hidden rounded-lg border">
      <div className="border-border bg-accent flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="space-y-1">
          <p className="text-foreground text-sm font-semibold">
            Quotes behind the projection
          </p>
          <p className="text-muted-foreground text-xs">
            {bandLabel} {formatCurrencyGbpCompact(bandTotal)} —{" "}
            <span className="text-foreground font-medium">
              {formatCurrencyGbpCompact(quotesTotal)}
            </span>{" "}
            from the {formatCount(quoteCount)} quote
            {quoteCount === 1 ? "" : "s"} below
            {baseline > 0 ? (
              <>
                , {formatCurrencyGbpCompact(baseline)} the unquoted baseline —
                customers who order without quoting first, so there is no quote
                to list
              </>
            ) : (
              <>, which the quotes below account for in full</>
            )}
            .
          </p>
          <p className="text-muted-foreground text-xs">
            These quotes are the open book as it stands today (
            {formatSnapshotDay(lines.as_of)}), recomputed rather than replayed.
            A quote won or lost since this morning’s snapshot therefore moves
            this table without moving the band above it, so the two can differ
            by a day’s trading as well as by the baseline.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs tabular-nums">
            {formatCount(lines.lines.length)} row
            {lines.lines.length === 1 ? "" : "s"}
            {hasMoreThanShown ? ` · showing first ${shown.length}` : ""}
          </span>
          <button
            type="button"
            onClick={onDownloadCsv}
            disabled={lines.lines.length === 0}
            className="border-border text-foreground hover:bg-muted/60 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60"
          >
            Download CSV
          </button>
        </div>
      </div>

      {lines.lines.length === 0 ? (
        <div className="text-muted-foreground p-8 text-center text-sm">
          No open quotes are projected into this month — the whole band is the
          unquoted baseline.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b">
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  Quote
                </th>
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase">
                  Customer
                </th>
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  Expected date
                </th>
                <th className="px-3 py-2.5 text-right font-semibold tracking-wide uppercase whitespace-nowrap">
                  Weighted value
                </th>
                <th className="px-3 py-2.5 text-right font-semibold tracking-wide uppercase whitespace-nowrap">
                  Weighted contribution
                </th>
                <th className="px-3 py-2.5 text-right font-semibold tracking-wide uppercase whitespace-nowrap">
                  Quote total this month ({metricWord})
                </th>
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  CSR
                </th>
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  Sales person
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line) => {
                const quoteTotal =
                  quoteTotals.get(line.estimate_id) ??
                  quoteLineAmount(line, metric);
                return (
                  <tr
                    key={`${line.estimate_id}-${line.expected_date}`}
                    className="border-border/60 hover:bg-muted/40 border-b last:border-0"
                  >
                    <td className="px-3 py-2.5 font-mono whitespace-nowrap">
                      {line._self ? (
                        <Link
                          to={line._self}
                          className="text-primary underline hover:no-underline"
                        >
                          {line.reference}
                        </Link>
                      ) : (
                        // The quote could not be resolved to an estimate row,
                        // so there is nothing to open. The line still shows:
                        // its money is in the band either way, and dropping it
                        // would make the table stop tying out.
                        <span className="text-foreground">
                          {line.reference}
                        </span>
                      )}
                    </td>
                    <td className="text-foreground px-3 py-2.5">
                      {line.customer_name || line.customer_id || "—"}
                    </td>
                    <td className="text-foreground px-3 py-2.5 whitespace-nowrap">
                      {formatSnapshotDay(line.expected_date)}
                    </td>
                    <td className="text-foreground px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums">
                      {formatCurrencyGbp(line.weighted_revenue)}
                    </td>
                    <td className="text-muted-foreground px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums">
                      {formatCurrencyGbp(line.weighted_contribution)}
                    </td>
                    <td className="text-foreground px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums">
                      {formatCurrencyGbp(quoteTotal)}
                    </td>
                    <td className="text-foreground px-3 py-2.5 whitespace-nowrap">
                      {line.csr_name || "—"}
                    </td>
                    <td className="text-foreground px-3 py-2.5 whitespace-nowrap">
                      {line.rep_name || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lines.lines.length > 0 && (
        <div className="border-border text-muted-foreground border-t px-4 py-2.5 text-xs">
          {hasMoreThanShown ? (
            <>
              Showing the first {shown.length} of{" "}
              {formatCount(lines.lines.length)} rows — <b>Download CSV</b> for
              every one.{" "}
            </>
          ) : null}
          Both money columns are <b>weighted</b>: a quote’s value after its
          bucket’s win-rate curve, so each is a fraction of what the quote is
          worth if it lands in full. A quote on several days is the curve
          spreading it — <b>Quote total this month</b> is that quote’s whole
          expected {metricWord} inside {monthLabel}, repeated on each of its
          rows.
        </div>
      )}
    </div>
  );
}
